import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ILocation } from '../../models/ILocation';
import { EmployeeCalendarSettings, IEmployee, IEmployeeLocationSettings } from '../../models/IEmployee';
import { calendarCellDurations, CellData } from '../constant';
import moment from 'moment-timezone';

interface LocationSettings {
    id: number;
    services: IEmployeeLocationSettings['services'];
    employees: number[];
}

interface CalendarFilterProps {
    selectedLocation: ILocation | null;
    selectedEmployee: IEmployee | null;
    selectedCustomerId: number | null;
    cellData: CellData;
    showScheduledStaffOnly: boolean;
    showCancelledAppointments: boolean;
    locationSettings: LocationSettings[] | null;
    allEmployees: IEmployee[];
    date: string;
    searchString: string;
    settingsInitialized: boolean;
    showSearch: boolean;
    busyEmployeeIds: number[];
    showArchivedStaff: boolean;
    showOnlyMe: boolean;
    employeeSettings: EmployeeCalendarSettings | null;
}

const initialState: CalendarFilterProps = {
    selectedLocation: null,
    selectedEmployee: null,
    selectedCustomerId: null,
    cellData: calendarCellDurations[60],
    showScheduledStaffOnly: false,
    showCancelledAppointments: true,
    locationSettings: [],
    allEmployees: [],
    date: moment().format(),
    searchString: '',
    settingsInitialized: false,
    showSearch: false,
    busyEmployeeIds: [],
    showArchivedStaff: true,
    showOnlyMe: false,
    employeeSettings: null
};

export const calendarFilterSlice = createSlice({
    name: 'calendarFilter',
    initialState,
    reducers: {
        setCalendarLocationFilter: (state, action: PayloadAction<ILocation | null>) => {
            state.selectedLocation = action.payload;
        },
        clearCalendarFilter: () => initialState,
        setCellData: (state, action: PayloadAction<CellData>) => {
            state.cellData = action.payload;
        },
        setShowScheduledStaffOnly: (state, action: PayloadAction<boolean>) => {
            state.showScheduledStaffOnly = action.payload;
        },
        setShowCancelledAppointments: (state, action: PayloadAction<boolean>) => {
            state.showCancelledAppointments = action.payload;
        },
        setLocationSettings: (state, action: PayloadAction<LocationSettings[] | null>) => {
            state.locationSettings = action.payload;
        },
        setAllEmployees: (state, action: PayloadAction<IEmployee[]>) => {
            state.allEmployees = action.payload;
        },
        setCalendarDate: (state, action: PayloadAction<string>) => {
            state.date = action.payload;
        },
        setSettingsInitialized: (state, action: PayloadAction<boolean>) => {
            state.settingsInitialized = action.payload;
        },
        setShowSearch: (state, action: PayloadAction<boolean>) => {
            state.showSearch = action.payload;
        },
        setBusyEmployeeIds: (state, action: PayloadAction<number[]>) => {
            state.busyEmployeeIds = action.payload;
        },
        setSelectedCustomerId: (state, action: PayloadAction<number | null>) => {
            state.selectedCustomerId = action.payload;
        },
        setShowArchivedStaff: (state, action: PayloadAction<boolean>) => {
            state.showArchivedStaff = action.payload;
        },
        setShowOnlyMe: (state, action: PayloadAction<boolean>) => {
            state.showOnlyMe = action.payload;
        },
        setLocalFilterSettings: (state, action: PayloadAction<EmployeeCalendarSettings | null>) => {
            state.employeeSettings = action.payload;
        }
    }
});

export const {
    setCalendarLocationFilter,
    clearCalendarFilter,
    setCellData,
    setShowScheduledStaffOnly,
    setShowCancelledAppointments,
    setLocationSettings,
    setAllEmployees,
    setCalendarDate,
    setSettingsInitialized,
    setShowSearch,
    setBusyEmployeeIds,
    setSelectedCustomerId,
    setShowArchivedStaff,
    setShowOnlyMe,
    setLocalFilterSettings
} = calendarFilterSlice.actions;
